import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  FurtherConsiderations,
  PanelField,
  Section,
  practitionerSectionTitle,
} from '../models';
import { AIExportSerializer } from './AIExportSerializer';
import { WBCEvaluationHelper } from './WBCEvaluationHelper';
import { GlucoseRegulationInsights } from '../components/bloodworkz/glucose-regulation-insights';
import { NutrientInsights } from '../components/bloodworkz/nutrient-insights';
import { StressAdrenalRegulationInsights } from '../components/bloodworkz/stress-adrenal-regulation-insights';
import { ViscosityInsights } from '../components/bloodworkz/viscosity-insights';
import {
  collectClientListProducts,
  readExcludedFromClientList,
  readSupplementEdits,
} from '../components/bloodworkz/recommended-supplements-panel';
import { supplementsVisible } from '../utils/supplementsVisibility';
import { digestionVisible } from '../utils/digestionVisibility';
import {
  GROUPS,
  SEVERITY,
  STATUS_LABEL,
  Status,
  changeArrow,
  sectionStatus,
} from '../components/bloodworkz/results-overview-panel';
import { MathUtils } from '../utils/MathUtils';
import { inlineHoverText } from '../utils/hoverText';
import { stripNavTokens } from '../utils/navToken';
import { AI_REVIEW_PROMPT } from './aiReviewPrompt';

// All sections whose tab content is emitted, in the order used by the export.
// A section held behind a visibility gate is skipped in the loop below rather
// than dropped from here, so this stays a complete inventory.
const ALL_SECTIONS = [
  Section.RBC, Section.WBC, Section.IRON, Section.PLATELETS,
  Section.GLUCOSE, Section.KIDNEY, Section.NUTRIENTS, Section.ELECTROLYTES,
  Section.PROTEINS, Section.DIGESTION, Section.LIVER_BILIARY, Section.LIPIDS,
  Section.INFLAMMATION_OXIDATIVE_STRESS, Section.THYROID,
  Section.STRESS_ADRENAL, Section.SEX_HORMONE, Section.PROSTATE,
];

// Maps each internal tab name (as stored on Consideration.tab) to the key used
// in the export. The Supplements TAB is intentionally OMITTED here: the only
// supplement content the file can carry is the practitioner's own curated
// client list (see buildRecommendedSupplements), never the by-pattern tables.
// About and References are handled separately below (they need per-marker /
// tag-resolution logic).
const TAB_KEY_MAP: Record<string, string> = {
  Interpretation:   'Insights',
  Causes:           'PossibleRootCauses',
  Testing:          'AdditionalTesting',
  Considerations:   'FurtherConsiderations',
  Symptoms:         'SignsAndSymptoms',
  Diet:             'Diet',
  Lifestyle:        'Lifestyle',
};

// Maps each Section to the BloodworkAnalysis getter that returns the panel
// markers displayed for it — mirrors how the UI feeds panelMarkers to each
// section's tabs (and thus the About tab). Explicit because the model has no
// generic section→markers dispatcher.
const SECTION_MARKER_GETTERS: Record<string, string> = {
  [Section.RBC]:                          'getRBCPanelMarkers',
  [Section.WBC]:                          'getWBCPanelMarkers',
  [Section.IRON]:                         'getIronPanelMarkers',
  [Section.PLATELETS]:                    'getPlateletPanelMarkers',
  [Section.GLUCOSE]:                      'getGlucosePanelMarkers',
  [Section.KIDNEY]:                       'getKidneyPanelMarkers',
  [Section.NUTRIENTS]:                    'getMineralPanelMarkers',
  [Section.ELECTROLYTES]:                 'getElectrolytePanelMarkers',
  [Section.PROTEINS]:                     'getProteinsPanelMarkers',
  [Section.DIGESTION]:                    'getDigestionPanelMarkers',
  [Section.LIVER_BILIARY]:                'getLiverPanelMarkers',
  [Section.LIPIDS]:                       'getLipidsPanelMarkers',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]:'getInflammationOxidativeStressPanelMarkers',
  [Section.THYROID]:                      'getThyroidPanelMarkers',
  [Section.STRESS_ADRENAL]:               'getStressAdrenalPanelMarkers',
  [Section.SEX_HORMONE]:                  'getSexHormonePanelMarkers',
  [Section.PROSTATE]:                     'getProstatePanelMarkers',
};

// The sections the Markers Outside Optimal buckets are built from — every
// section with a panel except Digestion (see buildMarkersOutsideOptimal).
const MOO_SECTIONS = Object.keys(SECTION_MARKER_GETTERS).filter(
  section => section !== Section.DIGESTION,
);

function sectionPanelMarkers(ba: BloodworkAnalysis, section: string): any[] {
  const getterName = SECTION_MARKER_GETTERS[section];
  const getter = getterName ? (ba as any)[getterName] : undefined;
  return typeof getter === 'function' ? getter.call(ba) || [] : [];
}

/**
 * The practitioner's own markers for one test: what they called it, what the
 * lab said, the ranges they entered (or LabSmarts derived), and where they
 * filed it. Flagged so the AI can weigh them differently from shipped markers —
 * nobody has validated these ranges.
 */
function buildPractitionerAddedMarkers(ba: BloodworkAnalysis): any | null {
  const markers = (ba.customPanelMarkers || []).filter(pm => pm.hasValue?.());
  // Markers the lab reports in words rather than numbers. Exported alongside
  // the charted ones because they are results on the same test — leaving a
  // urinalysis out would hand the model a report the practitioner can see and
  // it cannot. Kept in a SEPARATE list, and carrying no range, so nothing
  // reading this can mistake a word for a value with a position in a range.
  const textMarkers = (ba.customTextMarkers || []).filter(tm => tm.hasText?.());
  if (!markers.length && !textMarkers.length) {
    return null;
  }
  return {
    note:
      'Entered by the practitioner for this test, with reference ranges they supplied rather than LabSmarts-validated ones. A marker filed under a body system counts in that section exactly like a shipped marker — its Results Overview status and its Markers Outside Optimal entry alike; one left in its own section counts toward nothing.',
    markers: markers.map(pm => {
      const section = pm.field?.customSection;
      return {
        name: pm.field?.label,
        units: pm.field?.units,
        value: pm.value,
        percentFromMean:
          typeof pm.percentFromMean === 'number'
            ? MathUtils.round(pm.percentFromMean, 2)
            : undefined,
        // Where its bar graph is drawn: a body system, a section the
        // practitioner named, or the catch-all appendix.
        chartedIn: section
          ? SECTION_LABELS[section] || section
          : pm.field?.practitionerSection
          ? practitionerSectionTitle(pm.field.practitionerSection)
          : Section.CUSTOM_MARKERS,
        range: pm.range,
      };
    }),
    ...(textMarkers.length
      ? {
          textResults: {
            note:
              'Reported by the lab in words, not numbers (a urinalysis color, "Negative", a morphology comment). There is no reference range and no optimal value, so these are excluded from every score, tally and trend in this report. "expected" is the wording the practitioner said to expect, when they set one; a result that differs from it is marked on the report and is the only judgement made about these.',
            markers: textMarkers.map(tm => ({
              name: tm.label,
              result: tm.text,
              expected: tm.expected,
              differsFromExpected: tm.differsFromExpected?.(),
              chartedIn: tm.section
                ? SECTION_LABELS[tm.section] || tm.section
                : tm.practitionerSection
                ? practitionerSectionTitle(tm.practitionerSection)
                : Section.CUSTOM_MARKERS,
            })),
          },
        }
      : {}),
  };
}

// Serializable view of the RBC "Anemia Status" insight panel, mirroring what
// bloodwork-anemia-status.tsx renders (gauge + RBC size/color + the conditional
// text lines). Returns null when the RBC section has no markers to show.
function buildAnemiaInsights(ba: BloodworkAnalysis): any | null {
  if (sectionPanelMarkers(ba, Section.RBC).length === 0) return null;
  const anemia: any = {
    label: 'Suboptimal Blood Oxygen Delivery is',
    category: ba.anemicProbabilityCategory,
    probability: ba.anemicProbability,
    redBloodCellSize: ba.redBloodCellSize
      ? { type: ba.redBloodCellSize.type, name: ba.redBloodCellSize.name }
      : undefined,
    redBloodCellColor: ba.redBloodCellColor
      ? { type: ba.redBloodCellColor.type, name: ba.redBloodCellColor.name }
      : undefined,
  };
  if (ba.nutrientDeficiencyOrInflammationLikelyText) {
    anemia.nutrientDeficiencyOrInflammationLikelyText =
      ba.nutrientDeficiencyOrInflammationLikelyText;
  }
  if (ba.boneMarrowAnalysis?.boneMarrowOverproductionIsLikely) {
    anemia.boneMarrowOverproductionIsLikely = true;
  }
  const rootCauses = ba.possibleRootCauses ? ba.possibleRootCauses() : [];
  if (rootCauses && rootCauses.length > 0) {
    anemia.possibleRootCauses = rootCauses;
  }
  // The dehydration statement, whenever the analysis makes one — which is what
  // the RBC panel itself now does. It used to be suppressed here whenever the
  // hydration panel rendered, on the reasoning that the panel owned the sentence;
  // that panel is on hold, does not deploy and is not exported, and the RBC label
  // stays until it does (see HYDRATION_PANEL_OWNS_DEHYDRATION_TEXT). So the
  // condition described a handover that has not happened, and honoring it only
  // dropped the sentence from a local-dev export.
  if (ba.isAnyDehydrationPossibleOrHigher?.()) {
    anemia.dehydrationText = ba.deriveDehydrationText();
  }
  if (ba.isPoorOxygenationPossibleOrHigher?.()) {
    anemia.poorOxygenationText = ba.derivePoorOxygenationText();
  }
  if (ba.isPoorOxygenationDueToSmoking?.()) {
    anemia.poorOxygenationSmokerText = ba.derivePoorOxygenationTextForSmoker();
  }
  return anemia;
}

// Markers Outside Optimal (MOO) — the same 6-bucket grouping the UI/PDF show,
// so the AI can reference flagged markers by group without the export (or the AI)
// re-listing every value. Mirrors the filters in bloodwork-test-analysis.tsx.
// Returns readable short labels per bucket; empty buckets are omitted.
function buildMarkersOutsideOptimal(ba: BloodworkAnalysis): any | null {
  const seen = new Set<any>();
  const all: any[] = [];
  // Digestion is left out for the same reason bloodwork-test-analysis.tsx's
  // allMarkerSet leaves it out: every marker it renders is BORROWED from
  // another section, so each one is already counted through its own home and
  // adding the section could only introduce duplicates. The one thing it would
  // contribute is a practitioner-typed marker filed into Digestion — which the
  // screen does not list here either, and whose bar is not on screen at all
  // while the section is hidden.
  MOO_SECTIONS.forEach(section => {
    sectionPanelMarkers(ba, section).forEach(pm => {
      if (pm && !seen.has(pm)) {
        seen.add(pm);
        all.push(pm);
      }
    });
  });

  const label = (pm: any) => pm.field?.shortLabel || pm.field?.label || pm.attribute;
  const higherIsBetter = (pm: any) => pm.isEGFR?.() || pm.field?.useEGFRChart;
  const isRdw = (pm: any) => pm.field?.useRDWChart;
  const egfrBelowTier = (pm: any): number => {
    const r = pm.range;
    if (!r) return 3;
    if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
    if (r.labMin > 0 && pm.value < r.labMin) return 2;
    return 3;
  };
  const isHighSide = (pm: any) =>
    !higherIsBetter(pm) &&
    (pm.isGreaterThanAlarm?.() || pm.isGreaterThanLab?.() || pm.isGreaterThanOptimal?.());

  const withValue = all.filter(pm => pm.hasValue?.());
  const needsAttention = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
      : pm.isGreaterThanAlarm?.() || (!isRdw(pm) && pm.isLessThanAlarm?.()),
  );
  const worthDiscussing = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
      : (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
        (!isRdw(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.()),
  );
  const fineTune = withValue.filter(pm =>
    higherIsBetter(pm)
      ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
      : (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
        (!isRdw(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.()),
  );

  const buckets: Record<string, string[]> = {
    highAlarm: needsAttention.filter(isHighSide).map(label),
    lowAlarm: needsAttention.filter(pm => !isHighSide(pm)).map(label),
    aboveLab: worthDiscussing.filter(isHighSide).map(label),
    belowLab: worthDiscussing.filter(pm => !isHighSide(pm)).map(label),
    aboveOptimal: fineTune.filter(isHighSide).map(label),
    belowOptimal: fineTune.filter(pm => !isHighSide(pm)).map(label),
  };

  const result: Record<string, string[]> = {};
  Object.keys(buckets).forEach(k => {
    if (buckets[k].length > 0) result[k] = buckets[k];
  });
  return Object.keys(result).length > 0 ? result : null;
}

// Friendly section labels shown in the Results Overview panel (RBC → "Blood
// Oxygen", WBC → "Immune", etc.). Mirrors buildSections() in
// results-overview-panel.tsx.
const SECTION_LABELS: Record<string, string> = {
  [Section.RBC]: 'Blood Oxygen',
  [Section.IRON]: 'Iron Status',
  [Section.WBC]: 'Immune',
  [Section.PLATELETS]: 'Blood Clotting',
  [Section.GLUCOSE]: 'Glucose',
  [Section.KIDNEY]: 'Kidney',
  [Section.PROTEINS]: 'Proteins',
  [Section.NUTRIENTS]: 'Vitamins & Minerals',
  [Section.ELECTROLYTES]: 'Electrolytes',
  [Section.DIGESTION]: 'Digestion',
  [Section.LIVER_BILIARY]: 'Liver',
  [Section.LIPIDS]: 'Lipids',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]: 'Inflammation',
  [Section.THYROID]: 'Thyroid',
  [Section.STRESS_ADRENAL]: 'Stress & Adrenals',
  [Section.SEX_HORMONE]: 'Hormones',
  [Section.PROSTATE]: 'Prostate',
};

// Section markers as the Results Overview panel sees them — the Stress & Adrenals
// card excludes Na/K (those drive the adrenal gauge instead).
function resultsOverviewMarkers(ba: BloodworkAnalysis, section: string): any[] {
  const markers = sectionPanelMarkers(ba, section);
  if (section === Section.STRESS_ADRENAL) {
    return markers.filter(
      (pm: any) => pm.attribute !== 'sodium' && pm.attribute !== 'potassium',
    );
  }
  return markers;
}

// How many markers the improved/worsened judgement actually rests on: the ones
// carrying a value in BOTH tests. Mirrors the `inBoth` filter inside
// comparableStatusChange, which is module-private — the count is exported
// alongside the direction so a reader can tell "no change" from "nothing
// comparable to judge a change by", which is the whole point of the guard.
function comparableMarkerCount(current: any[], previous: any[]): number {
  const valued = (markers: any[]) =>
    new Set(markers.filter(pm => pm.hasValue?.()).map(pm => pm.attribute));
  const currentValued = valued(current);
  const previousValued = valued(previous);
  let count = 0;
  currentValued.forEach(attribute => {
    if (previousValued.has(attribute)) count += 1;
  });
  return count;
}

// Results Overview: the per-section color the practitioner sees, plus a rollup
// into the 5 area groups (Fuel & Energize, etc.) with a per-system score. Gives
// the AI a high-level status for EVERY body system — including ones with no
// detailed pattern engine. Colors and grouping reuse results-overview-panel.tsx
// so this stays in lockstep with the UI; the per-system score is new (there is
// none in the UI): 0–100, higher = healthier, set by the WORST section in the
// group (worst-wins, matching worstColor and the panel) — green 100, yellow 67,
// orange 33, red 0. Deliberately NOT an average: one flagged section must not be
// diluted by however many healthy sections happen to sit alongside it.
function buildResultsOverview(ba: BloodworkAnalysis): any | null {
  const sections: Record<string, any> = {};
  const colorOf: Record<string, Status> = {};
  // Set by preparePreviousPanelMarkers — the test before this one, which is
  // what the improved/worsened arrow is measured against.
  const previous = ba.previousAnalysis;

  Object.keys(SECTION_MARKER_GETTERS).forEach(section => {
    // Digestion carries a Results Overview box only while the section is
    // switched on — `show: digestionVisible()` in buildSections(). Without this
    // the export scored a box the practitioner has no way to see, and because
    // every Digestion marker is borrowed from elsewhere (ferritin from Iron,
    // ALP from Liver, the minerals from Nutrients) it dragged the worst-wins
    // Digest & Build score red off markers that live in other groups entirely.
    if (section === Section.DIGESTION && !digestionVisible()) return;
    // The Immune box follows the WBC COUNT, not "anything in the WBC panel" —
    // `show: hasWbcValue` in buildSections(), where hasWbcValue is wbc
    // .hasValue(). The panel also renders markers borrowed from elsewhere
    // (globulin, for one), so scoring on "any marker has a value" published an
    // Optimal immune pill for a test where no WBC was ordered.
    if (section === Section.WBC && !ba.wbc?.hasValue?.()) return;
    const markers = resultsOverviewMarkers(ba, section);
    if (!markers.some((pm: any) => pm.hasValue?.())) return; // untested → skip
    const color = sectionStatus(ba, section as any, markers);
    const entry: any = {
      label: SECTION_LABELS[section] || section,
      color,
      status: STATUS_LABEL[color], // pill word: Optimal | Monitor | Support | Priority
    };

    // The same improved/worsened thumb the section row carries, from the same
    // function, so the export cannot say something the report doesn't.
    // changeArrow ANDs the visible pill's direction with the direction of the
    // markers measured in BOTH tests, which is what stops a marker that was
    // simply ordered this time (or dropped) from reading as a change in the
    // client — see comparableStatusChange.
    if (previous) {
      const prevMarkers = resultsOverviewMarkers(previous, section);
      const prevStatus =
        prevMarkers.length > 0
          ? sectionStatus(previous, section as any, prevMarkers)
          : null;
      if (prevStatus !== null) {
        entry.changeVsPrevious =
          changeArrow({
            section: section as any,
            status: color,
            prevStatus,
            markers,
            prevMarkers,
          }) || 'none';
        entry.comparedOnMarkers = comparableMarkerCount(markers, prevMarkers);
      }
    }

    sections[section] = entry;
    colorOf[section] = color;
  });

  if (Object.keys(sections).length === 0) return null;

  const systems = GROUPS.map(group => {
    const tested = group.sectionKeys.filter(s => colorOf[s] !== undefined);
    if (tested.length === 0) {
      return {
        name: group.name,
        subtitle: group.subtitle,
        sections: [],
        worstColor: 'notAssessed',
        worstStatus: 'Not assessed',
        score: null,
      };
    }
    let worst = tested[0];
    tested.forEach(s => {
      if (SEVERITY[colorOf[s]] < SEVERITY[colorOf[worst]]) worst = s;
    });
    // Worst-wins, NOT an average: the score is the worst section's severity
    // mapped to 0–100 (green 100, yellow 67, orange 33, red 0). Section count
    // is irrelevant — 1 red out of 5 scores the same as 1 red out of 2.
    const score = Math.round((SEVERITY[colorOf[worst]] / 3) * 100);
    return {
      name: group.name,
      subtitle: group.subtitle,
      sections: tested,
      worstColor: colorOf[worst],
      worstStatus: STATUS_LABEL[colorOf[worst]],
      score,
    };
  });

  return { sections, systems };
}

/**
 * The practitioner's OWN curated client list for this test — the Recommended
 * Supplements card, which is name / description / dose and nothing else.
 *
 * This is the one piece of supplement content the export carries, and it is
 * here because it is a DECISION rather than a catalog: it says what the
 * practitioner already has this client taking, which is what lets a review say
 * "the protocol covers the glucose picture but nothing addresses the ferritin".
 * The by-system tables and the Holobiome roadmap stay out — the first is
 * re-derivable from the pattern data already exported, and the second is a
 * product-coverage map that reads the same for every client.
 *
 * Opt-in per caller, NOT unconditional, because buildAIExportData also builds
 * the payload for the client-facing "Overall Progress & Trends" draft. Product
 * names have no business in text that prints on the client's report, so only
 * the practitioner's raw-data download asks for this.
 *
 * `link` is dropped on the way through: it is an affiliate URL, which is
 * practitioner-only content and nothing a review needs.
 */
function buildRecommendedSupplements(
  ba: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands?: string[],
): any | null {
  if (!supplementsVisible()) return null;
  const excluded = readExcludedFromClientList(
    ba,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const products = collectClientListProducts(
    ba,
    furtherConsiderations,
    enabledSupplementBrands,
    excluded,
    readSupplementEdits(ba),
  );
  if (!products.length) return null;
  return {
    note:
      "The practitioner's own recommended supplements for this test, as the client sees them. Context for what is already being addressed — do not rank, substitute or add to them, and do not recommend a supplement that is not on this list.",
    products: products.map(product => ({
      name: product.name,
      description: product.description,
      dose: product.dose,
    })),
  };
}

// BMI value + standard adult category (matches MathUtils.bmi and the BMI panel).
// Uses the client snapshot's height/weight, falling back to the test's. Adult
// thresholds; the client's age/sex are in the export for pediatric context.
function buildBmi(ba: BloodworkAnalysis): any | null {
  const bw: any = ba.bloodwork || {};
  const client: any = bw.client || {};
  const heightCm =
    client.height != null ? Number(client.height)
      : bw.height != null ? Number(bw.height) : null;
  const weightLbs =
    client.weight != null ? Number(client.weight)
      : bw.weight != null ? Number(bw.weight) : null;
  if (heightCm == null || weightLbs == null || !heightCm || isNaN(heightCm) || isNaN(weightLbs)) {
    return null;
  }
  const raw = MathUtils.bmi(weightLbs, heightCm);
  if (raw == null || isNaN(raw)) return null;
  const value = Math.round(raw * 10) / 10;
  const category =
    value < 18.5 ? 'Underweight'
      : value < 25 ? 'Normal'
        : value < 30 ? 'Overweight'
          : 'Obese';
  return { value, category };
}

export interface AIExportData {
  // AI-review prompt, placed first so it sits at the TOP of the exported file.
  instructions: string;
  // The practitioner's client-level "Overall Practitioner Notes" (current value).
  overallPractitionerNotes?: string;
  analysisHistory: any[];
}

// The ▲/▼ direction glyphs ride into the export on the DB "Logic:" lines (e.g.
// "▲ WBC, ▲ NLR") and a few insight fields. Spell them out so the arrows are
// pure ASCII — clearer for the AI and immune to any glyph-encoding mishap on the
// reader's side. (µ in units is intentionally kept; the download declares UTF-8
// so it survives.) Walks every string in the export object.
function spellOutDirectionGlyphs<T>(value: T): T {
  if (typeof value === 'string') {
    return value.replace(/▲/g, 'high').replace(/▼/g, 'low') as any;
  }
  if (Array.isArray(value)) {
    return value.map(spellOutDirectionGlyphs) as any;
  }
  if (value && typeof value === 'object') {
    const out: any = {};
    Object.keys(value as any).forEach(k => {
      out[k] = spellOutDirectionGlyphs((value as any)[k]);
    });
    return out;
  }
  return value;
}

/**
 * Build the raw-data export object for the "Export Raw Data" feature. Pure and
 * side-effect free (no DOM / download) so it can be unit-tested; the component's
 * exportJson() just serializes and downloads what this returns.
 *
 * Per test it includes:
 *  - serialized markers + ranges (the Reference Range tab / marker table);
 *  - every RELEASED insight panel: RBC/anemia (anemiaInsights), immune
 *    (wbcEvaluation + immuneGauge), glucose (glucosePatterns + glucoseGauge),
 *    Vitamins & Minerals (nutrientInsights) and Stress & Adrenal
 *    (stressAdrenalInsights). Thyroid and Hydration are ON HOLD and are exported
 *    NOWHERE, even in the builds that still render them — see the comment at
 *    their call site. Blood Clotting & Flow's computed viscosity card rides in
 *    tabsContent as the Platelets Insights tab;
 *  - Results Overview (resultsOverview), including each section's guarded
 *    improved/worsened change against the previous collection, and BMI (bmi);
 *  - per-section tab content (tabsContent) for EVERY displayed tab except
 *    Supplements — Insights, PossibleRootCauses, AdditionalTesting,
 *    FurtherConsiderations, SignsAndSymptoms, Diet, Lifestyle, References
 *    (citations resolved), and About (per-marker descriptions);
 *  - the practitioner's Recommended Supplements client list
 *    (recommendedSupplements), and ONLY where the caller opts in — see the
 *    option's docblock for why that is not unconditional;
 *  - every practitioner note across all sections (notes), except the
 *    Recommended Supplements exclusion list, which is supplement content
 *    rather than prose.
 *
 * `overallPractitionerNotes` is the client-level "Overall Practitioner Notes" and
 * must be passed in from the LIVE client — it is a single current value, NOT read
 * from the per-test client snapshots (which freeze whatever the note was when each
 * test was saved), so a practitioner edit/delete is reflected on the next export.
 */
export function buildAIExportData(
  bloodworks: Bloodwork[],
  bloodworkRanges: BloodworkRange[],
  furtherConsiderations: FurtherConsiderations,
  overallPractitionerNotes?: string,
  options: {
    /**
     * Emit the practitioner's Recommended Supplements client list per test.
     * OFF by default: the client-facing "Overall Progress & Trends" draft is
     * built from this same function, and product names must not have a route
     * into text that prints on the client's report. Only the practitioner's
     * raw-data download turns it on.
     */
    includeRecommendedSupplements?: boolean;
    /** The practitioner's enabled supplement brands, for the list above. */
    enabledSupplementBrands?: string[];
  } = {},
): AIExportData {
  const analysisFor = (bw: Bloodwork): BloodworkAnalysis =>
    new BloodworkAnalysis({
      bloodwork: bw,
      bloodworkRanges,
      bloodworkFields: PanelField.ALL_FIELDS,
    });

  // Which test came before which, by DATE rather than by array position: the
  // caller's order is not something to lean on (the comparison screen reverses
  // its own copy in place), and "previous" is a fact about the collection
  // dates. Ties break on createDate, the same pair the store sorts on.
  const chronological = [...bloodworks].sort((a, b) =>
    (a.collectionDate || '').localeCompare(b.collectionDate || '') ||
    (a.createDate || '').localeCompare(b.createDate || ''),
  );
  const previousOf = new Map<Bloodwork, Bloodwork>();
  chronological.forEach((bw, i) => {
    if (i > 0) previousOf.set(bw, chronological[i - 1]);
  });

  const bloodworkAnalysisHistory = bloodworks.map(bw => {
    const ba = analysisFor(bw);
    ba.init().performAnalysis();

    // Link each test to the one before it, exactly as the analysis screen does
    // (current init()+performAnalysis(), previous init() only —
    // preparePreviousPanelMarkers runs performAnalysis on it itself). Without
    // this every test was analyzed as if it were the client's first: no
    // previousPanelMarker on any marker, and no improved/worsened arrow on any
    // Results Overview section.
    //
    // A FRESH previous instance per link, never a reused one from this history:
    // preparePreviousPanelMarkers rebuilds the marker objects on both analyses,
    // so feeding it an instance that already carries its own previous link
    // would tear that link back off — chaining A→B→C would leave B holding
    // nothing.
    const previousBloodwork = previousOf.get(bw);
    if (previousBloodwork) {
      const previousAnalysis = analysisFor(previousBloodwork);
      previousAnalysis.init();
      ba.preparePreviousPanelMarkers?.(previousAnalysis);
    }
    return ba;
  });

  const analysisHistory = bloodworkAnalysisHistory.map(ba => {
      const jsonString = JSON.stringify(ba, AIExportSerializer.replacer);
      const json = JSON.parse(jsonString);

      // Add WBC (immune) pattern data
      const wbcEvaluation = WBCEvaluationHelper.evaluate(ba);
      if (Object.keys(wbcEvaluation).length > 0) {
        (json as any).wbcEvaluation = wbcEvaluation;
      }

      // Immune gauge likelihood (mirrors the Immune Activity Insights panel)
      if (ba.wbc?.hasValue?.()) {
        (json as any).immuneGauge = {
          label: 'Immune System Abnormality is',
          category: ba.wbcAnalysis?.immuneSystemDisorderProbabilityCategory,
          probability: ba.wbcAnalysis?.immuneSystemDisorderProbability,
        };
      }

      // Add Glucose Regulation pattern insights (mirrors the panel; no supplements)
      const glucosePatterns = GlucoseRegulationInsights.exportPatterns(ba);
      if (glucosePatterns.length > 0) {
        (json as any).glucosePatterns = glucosePatterns;
      }

      // Glucose gauge likelihood (mirrors the Glucose Regulation Insights panel)
      if (GlucoseRegulationInsights.shouldRender(ba)) {
        (json as any).glucoseGauge = {
          label: 'Glucose Dysregulation is',
          category: ba.glucoseAnalysis?.glucoseRegulationProbabilityCategory,
          probability: ba.glucoseAnalysis?.glucoseRegulationProbability,
        };
      }

      // RBC / Anemia Status insight panel
      const anemiaInsights = buildAnemiaInsights(ba);
      if (anemiaInsights) {
        (json as any).anemiaInsights = anemiaInsights;
      }

      // Vitamin & Mineral Insights — tally, Focus list, one-carbon pattern
      // boxes and open questions. Released to prod on 2026-08-31, so it is
      // exported unconditionally like the glucose and immune panels. Its
      // pattern boxes carry their own gate inside patternIds(), so a build that
      // hides them exports none.
      const nutrientInsights = NutrientInsights.exportInsights(ba);
      if (nutrientInsights) {
        (json as any).nutrientInsights = nutrientInsights;
      }

      // Stress & Adrenal Insights — gauge plus pattern boxes. Also released to
      // prod on 2026-08-31.
      const stressAdrenalInsights = StressAdrenalRegulationInsights.exportInsights(ba);
      if (stressAdrenalInsights) {
        (json as any).stressAdrenalInsights = stressAdrenalInsights;
      }

      // Thyroid and Hydration are ON HOLD and are NOT exported — not in any
      // build, including the ones that still render their panels (Thyroid on
      // slava, Hydration in local dev). This is deliberately STRICTER than the
      // "same gate as the panel" rule the released panels follow: a panel on
      // screen is read by the practitioner who knows it is unreviewed, while
      // the export is read by an AI whose summary is then read as a finding.
      // Held content should not be able to become one.
      //
      // TO RESTORE, once the panel is released: call its exportInsights() here
      // the way nutrientInsights and stressAdrenalInsights are called above,
      // and add the block to the schema in aiReviewPrompt.ts AND
      // docs/ai-review-prompt.md (which is an abbreviated variant, not a copy).

      // The practitioner's own markers, named. Emitted here rather than left to
      // the generic serializer, which strips field/attribute and reduced them to
      // an anonymous list of numbers.
      const practitionerAddedMarkers = buildPractitionerAddedMarkers(ba);
      if (practitionerAddedMarkers) {
        (json as any).practitionerAddedMarkers = practitionerAddedMarkers;
      }

      // Markers Outside Optimal (MOO) — flagged markers grouped by severity,
      // so the summary can reference them without re-listing every value.
      const markersOutsideOptimal = buildMarkersOutsideOptimal(ba);
      if (markersOutsideOptimal) {
        (json as any).markersOutsideOptimal = markersOutsideOptimal;
      }

      // Results Overview — per-section color + the 5 area-group rollup with a
      // per-system score. High-level status for every body system.
      const resultsOverview = buildResultsOverview(ba);
      if (resultsOverview) {
        (json as any).resultsOverview = resultsOverview;
      }

      // The collection this test's improved/worsened arrows are measured
      // against, so a reader can tell which pair of tests a change refers to
      // without inferring it from the date ordering.
      const previousCollectionDate = ba.previousBloodwork?.collectionDate;
      if (previousCollectionDate) {
        (json as any).previousCollectionDate = previousCollectionDate;
      }

      // BMI value + category (from the test's height/weight snapshot)
      const bmi = buildBmi(ba);
      if (bmi) {
        (json as any).bmi = bmi;
      }

      // The practitioner's curated client list — only where the caller asked
      // for it (see the option's docblock).
      if (options.includeRecommendedSupplements) {
        const recommendedSupplements = buildRecommendedSupplements(
          ba,
          furtherConsiderations,
          options.enabledSupplementBrands,
        );
        if (recommendedSupplements) {
          (json as any).recommendedSupplements = recommendedSupplements;
        }
      }

      // Every practitioner note across all sections (body-system sections plus
      // Results Overview, the general Notes tab, BMI, etc.), keyed by section.
      const notes: Record<string, string> = {};
      (ba.bloodwork.bloodworkNotes?.notes || []).forEach((n: any) => {
        // The same exclusion AIExportSerializer.replacer applies to
        // bloodwork.bloodworkNotes, and for the same reason — it was applied
        // there and not here, so the note the serializer stripped came straight
        // back at the top level. The Recommended Supplements "note" is not
        // prose: it is the list of products the practitioner UNTICKED, stored
        // in the notes array because that is where per-test state lives. Under
        // a section heading it reads to a model as a list the practitioner
        // WROTE, with the meaning inverted, and it is supplement content in an
        // export that promises to carry none.
        if (n?.section === Section.RECOMMENDED_SUPPLEMENTS) return;
        if (n?.text?.trim()) {
          notes[n.section] = n.text.trim();
        }
      });
      if (Object.keys(notes).length > 0) {
        (json as any).notes = notes;
      }

      // Build tab content for every section that has root-cause categories
      const tabsContent: Record<string, any> = {};

      ALL_SECTIONS.forEach(section => {
        // Digestion is held on its own switch (report body, PDF, Results
        // Overview and comparison table all read it), so its tab content is
        // held with it — the export describes the report the practitioner has,
        // and a section they cannot open is not part of that report. Reading
        // digestionVisible() rather than an env check of its own means the
        // section and its export reopen in one edit.
        if (section === Section.DIGESTION && !digestionVisible()) {
          return;
        }

        const categories = ba.possibleRootCauseCategoriesFor
          ? ba.possibleRootCauseCategoriesFor(section) || []
          : [];

        const sectionContent: Record<string, any> = {};

        Object.entries(TAB_KEY_MAP).forEach(([internalTab, exportKey]) => {
          const considerations: Array<{ title: string; items: string[] }> = [];
          categories.forEach(cat => {
            furtherConsiderations
              .findConsiderations(cat.rootCauseCategory, section, internalTab)
              // Fold any "[Hover text: …]" explainer into the bullet — the
              // export is plain data for the model, so the annotation should
              // read as part of the sentence rather than as raw markup.
              .forEach(c =>
                considerations.push({
                  title: c.title,
                  // Plain text for a model to read: fold in the hover
                  // explainer and reduce any cross-section pointer to its
                  // label, so neither markup reaches the export.
                  items: (c.items || []).map(it =>
                    stripNavTokens(inlineHoverText(it)),
                  ),
                }),
              );
          });
          if (considerations.length > 0) {
            sectionContent[exportKey] = considerations;
          }
        });

        // Blood Clotting & Flow's Insights tab is a COMPUTED viscosity card, not
        // a FurtherConsiderations "Interpretation" row, so the loop above finds
        // nothing for it and the tab was missing from the export entirely.
        // digestGroup() is the same plain-text form the Notes "gather from
        // tabs" digest uses, so the export and the digest say the same thing.
        // Guarded: a compute() throw must cost the viscosity card, not the
        // whole export (same guard buildSectionDigest applies).
        if (section === Section.PLATELETS) {
          let viscosity: { title: string; items: string[] } | null = null;
          try {
            viscosity = ViscosityInsights.digestGroup(ba);
          } catch (e) {
            viscosity = null;
          }
          if (viscosity) {
            sectionContent.Insights = [
              ...((sectionContent.Insights as any[]) || []),
              viscosity,
            ];
          }
        }

        // References tab — items are Reference Library tag slugs; resolve each to
        // its full citation (findByTag -> library row) so the export carries
        // readable citations, not opaque slugs. Falls back to the raw item when a
        // tag has no library row (legacy inline citations).
        const references: Array<{ title: string; items: string[] }> = [];
        categories.forEach(cat => {
          furtherConsiderations
            .findConsiderations(cat.rootCauseCategory, section, 'References')
            .forEach(c => {
              const items = (c.items || []).map(item => {
                const libraryRow = furtherConsiderations.findByTag(item);
                return libraryRow?.items?.[0] ?? item;
              });
              references.push({ title: c.title, items });
            });
        });
        if (references.length > 0) {
          sectionContent.References = references;
        }

        // About tab — per-marker plain-language descriptions, resolved exactly
        // as the About panel does (findAboutConsideration keyed off each
        // displayed marker's field id), deduped by categoryId.
        const aboutSeen: Record<number, boolean> = {};
        const about: Array<{ title: string; items: string[] }> = [];
        sectionPanelMarkers(ba, section).forEach((pm: any) => {
          if (!pm || !pm.field || (pm.hasValue && !pm.hasValue())) return;
          const a = furtherConsiderations.findAboutConsideration(section, pm.field.id);
          if (a && a.items && a.items.length > 0 && !aboutSeen[a.categoryId]) {
            aboutSeen[a.categoryId] = true;
            about.push({ title: a.title, items: a.items });
          }
        });
        if (about.length > 0) {
          sectionContent.About = about;
        }

        if (Object.keys(sectionContent).length > 0) {
          tabsContent[section] = sectionContent;
        }
      });

      if (Object.keys(tabsContent).length > 0) {
        (json as any).tabsContent = tabsContent;
      }

      // Spell out ▲/▼ as high/low across everything assembled above.
      return spellOutDirectionGlyphs(json);
  });

  const notes = (overallPractitionerNotes || '').trim();
  return {
    // First key → serializes at the top of the file, so the AI reads it first.
    instructions: AI_REVIEW_PROMPT,
    // Client-level "Overall Practitioner Notes" from the LIVE client (NOT a stale
    // per-test snapshot). It's a client-level field, so it appears ONCE here and is
    // never attached to — or removed from — an individual test.
    ...(notes ? { overallPractitionerNotes: notes } : {}),
    analysisHistory,
  };
}
